import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, Typography, Button, Select, MenuItem,
  IconButton, Alert, CircularProgress, Skeleton, Chip, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Avatar
} from '@mui/material';
import {
  Sms, Refresh, Replay, ErrorOutline,
  CheckCircleOutline, HourglassEmpty, Cancel, Send,
  PhoneAndroid, DesktopWindows, DeleteOutline, Close,
  Add, SyncAlt, Message, Router, Settings
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useApp } from '../context/AppContext';
import api from '../api/authApi';
import { useNavigate } from 'react-router-dom';

const isMobile = () => /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
const ROUTER_HELPER_KEY = 'microdairy.routerSmsHelperUrl';
const DEFAULT_ROUTER_HELPER = 'http://127.0.0.1:17840';

const normaliseHelperUrl = (value) => (value || DEFAULT_ROUTER_HELPER).trim().replace(/\/$/, '');

const STATUS_CONFIG = {
  PENDING:   { label: 'Pending',   color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: <HourglassEmpty sx={{ fontSize: 12 }} /> },
  SENT:      { label: 'Sent',      color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', icon: <Send           sx={{ fontSize: 12 }} /> },
  DELIVERED: { label: 'Delivered', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', icon: <CheckCircleOutline sx={{ fontSize: 12 }} /> },
  FAILED:    { label: 'Failed',    color: '#dc2626', bg: '#fef2f2', border: '#fecaca', icon: <Cancel         sx={{ fontSize: 12 }} /> },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
  return (
    <Chip icon={cfg.icon} label={cfg.label} size="small"
      sx={{ fontWeight: 700, fontSize: '0.68rem', height: 20,
        color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
        '& .MuiChip-icon': { color: `${cfg.color} !important`, ml: '3px' } }} />
  );
}

function StatCard({ label, value, color, bg, icon }) {
  return (
    <Box sx={{ flex: 1, minWidth: 70, px: 1.2, py: 1, borderRadius: 2, textAlign: 'center',
      background: bg || '#fff', border: `1.5px solid ${color}22` }}>
      <Box sx={{ color, mb: 0.2 }}>{icon}</Box>
      <Typography sx={{ fontWeight: 900, color, lineHeight: 1.1, fontSize: '1.05rem' }}>{value}</Typography>
      <Typography variant="caption" sx={{ color: '#888', fontSize: '0.63rem' }}>{label}</Typography>
    </Box>
  );
}

function sendSmsViaSim(mobile, message) {
  const isIos = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const sep = isIos ? '&' : '?';
  const uri = `sms:${mobile}${sep}body=${encodeURIComponent(message)}`;
  const a = document.createElement('a');
  a.href = uri;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => document.body.removeChild(a), 1000);
}

export default function SmsLogs() {
  const { theme } = useApp();
  const navigate = useNavigate();
  const tc = theme?.primary || '#1a7a4a';

  const [filterStatus, setFilterStatus] = useState('');
  const [page,         setPage]         = useState(1);
  const [pageSize,     setPageSize]     = useState(20);
  const [data,         setData]         = useState([]);
  const [totalCount,   setTotalCount]   = useState(0);
  const [loading,      setLoading]      = useState(false);
  const [stats,        setStats]        = useState(null);
  const [retrying,     setRetrying]     = useState(false);
  const [resetting,    setResetting]    = useState(false);
  const [sending,      setSending]      = useState(false);
  const [currentIdx,   setCurrentIdx]   = useState(0);
  const [pendingList,  setPendingList]  = useState([]);
  const [success,      setSuccess]      = useState('');
  const [error,        setError]        = useState('');
  const [syncingRates, setSyncingRates] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);
  const [routerOnline, setRouterOnline] = useState(false);
  const [routerBusy,   setRouterBusy]   = useState(false);
  const [routerUrl,    setRouterUrl]    = useState(() =>
    normaliseHelperUrl(localStorage.getItem(ROUTER_HELPER_KEY))
  );
  const [routerUrlDraft, setRouterUrlDraft] = useState(() =>
    normaliseHelperUrl(localStorage.getItem(ROUTER_HELPER_KEY))
  );
  const [routerSettingsOpen, setRouterSettingsOpen] = useState(false);

  const showMsg = (msg, isErr = false) => {
    if (isErr) setError(msg); else setSuccess(msg);
    setTimeout(() => { setSuccess(''); setError(''); }, 4000);
  };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (filterStatus) params.append('status', filterStatus);
      const res = await api.get(`/sms/logs?${params}`);
      if (res.data?.success) {
        setData(res.data.data?.data || []);
        setTotalCount(res.data.data?.totalCount || 0);
      }
    } catch { }
    setLoading(false);
  }, [page, pageSize, filterStatus]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/sms/stats');
      if (res.data?.success) setStats(res.data.data);
    } catch { }
  }, []);

  const fetchPending = useCallback(async () => {
    try {
      const res = await api.get('/sms/logs?page=1&pageSize=200&status=PENDING');
      if (res.data?.success) setPendingList(res.data.data?.data || []);
    } catch { }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchStats(); fetchPending(); }, [fetchStats, fetchPending]);

  const checkRouter = useCallback(async (url = routerUrl, quiet = true) => {
    try {
      const response = await fetch(`${normaliseHelperUrl(url)}/health`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal: AbortSignal.timeout(3000),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json().catch(() => ({}));
      const ready = result.ready !== false && result.routerConnected !== false;
      setRouterOnline(ready);
      if (!quiet) showMsg(ready ? 'Router SMS sender is online.' : 'Router is not connected.', !ready);
      return ready;
    } catch {
      setRouterOnline(false);
      if (!quiet) showMsg('Router SMS sender is offline. Start the local sender and check its address.', true);
      return false;
    }
  }, [routerUrl]);

  useEffect(() => {
    checkRouter();
    const timer = setInterval(() => checkRouter(), 15000);
    return () => clearInterval(timer);
  }, [checkRouter]);

  const refresh = () => { fetchLogs(); fetchStats(); fetchPending(); };

  const sendAllViaSim = async () => {
    if (!isMobile()) { showMsg('Open on mobile to send via SIM.', true); return; }
    if (!pendingList.length) { showMsg('No pending SMS.'); return; }
    setSending(true);
    for (let i = 0; i < pendingList.length; i++) {
      setCurrentIdx(i + 1);
      const sms = pendingList[i];
      sendSmsViaSim(sms.mobileNumber, sms.message);
      try { await api.post('/sms/mark-sent', { smsId: sms.smsId, sentBy: 'Mobile-SIM' }); } catch { }
      await new Promise(r => setTimeout(r, 3000));
    }
    setSending(false);
    showMsg(`${pendingList.length} SMS sent! ✅`);
    refresh();
  };

  const sendSingle = async (row) => {
    if (!isMobile()) { showMsg('Open on mobile to send via SIM.', true); return; }
    sendSmsViaSim(row.mobileNumber, row.message);
    try {
      await api.post('/sms/mark-sent', { smsId: row.smsId, sentBy: 'Mobile-SIM' });
      showMsg(`SMS sent to ${row.mobileNumber} ✅`);
      refresh();
    } catch { }
  };

  const sendThroughRouter = async (row, quiet = false) => {
    const response = await fetch(`${routerUrl}/api/sms/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        smsId: row.smsId,
        mobileNumber: row.mobileNumber,
        message: row.message,
      }),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || result.success === false) {
      throw new Error(result.message || result.error || `Router sender returned HTTP ${response.status}`);
    }
    await api.post('/sms/mark-sent', { smsId: row.smsId, sentBy: 'Secureye-Router' });
    if (!quiet) showMsg(`SMS sent to ${row.mobileNumber} through router.`);
  };

  const sendSingleViaRouter = async (row) => {
    setRouterBusy(true);
    try {
      if (!(await checkRouter(routerUrl, true))) throw new Error('Router SMS sender is offline.');
      await sendThroughRouter(row);
      refresh();
    } catch (e) {
      showMsg(e.message || 'Router SMS failed.', true);
      setRouterOnline(false);
    } finally {
      setRouterBusy(false);
    }
  };

  const sendAllViaRouter = async () => {
    if (!pendingList.length) { showMsg('No pending SMS.'); return; }
    setRouterBusy(true);
    setCurrentIdx(0);
    let sent = 0;
    let failed = 0;
    try {
      if (!(await checkRouter(routerUrl, true))) throw new Error('Router SMS sender is offline.');
      for (let i = 0; i < pendingList.length; i++) {
        setCurrentIdx(i + 1);
        try {
          await sendThroughRouter(pendingList[i], true);
          sent++;
        } catch {
          failed++;
        }
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      showMsg(`${sent} SMS sent through router${failed ? `; ${failed} failed` : ''}.`, failed > 0);
      refresh();
    } catch (e) {
      showMsg(e.message || 'Router SMS sender is offline.', true);
      setRouterOnline(false);
    } finally {
      setRouterBusy(false);
      setCurrentIdx(0);
    }
  };

  const saveRouterSettings = async () => {
    const value = normaliseHelperUrl(routerUrlDraft);
    localStorage.setItem(ROUTER_HELPER_KEY, value);
    setRouterUrl(value);
    setRouterUrlDraft(value);
    setRouterSettingsOpen(false);
    setTimeout(() => checkRouter(value, false), 0);
  };

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const res = await api.post('/sms/retry');
      if (res.data?.success) { showMsg('Retry triggered!'); setTimeout(refresh, 2000); }
      else showMsg(res.data?.message || 'Retry failed.', true);
    } catch { showMsg('Retry failed.', true); }
    setRetrying(false);
  };

  const handleResetFailed = async () => {
    setResetting(true);
    try {
      const res = await api.post('/sms/reset-failed');
      if (res.data?.success) { showMsg('Failed SMS reset to pending.'); refresh(); }
      else showMsg(res.data?.message || 'Reset failed.', true);
    } catch { showMsg('Reset failed.', true); }
    setResetting(false);
  };

  const handleSyncRates = async () => {
    setSyncingRates(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await api.post('/collection/update-rates', { date: today });
      if (res.data?.success) showMsg(res.data.message || 'Rates synced for today! ✅');
      else showMsg(res.data?.message || 'Sync failed.', true);
    } catch { showMsg('Rate sync failed.', true); }
    setSyncingRates(false);
  };

  const confirmDelete = (row) => { setDeleteTarget(row); setDeleteDialog(true); };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await api.delete(`/sms/${deleteTarget.smsId}`);
      if (res.data?.success) {
        showMsg('SMS deleted ✅');
        setDeleteDialog(false); setDeleteTarget(null);
        refresh();
      } else {
        showMsg(res.data?.message || 'Delete failed. Ensure DELETE /api/sms/{id} exists.', true);
      }
    } catch (e) {
      showMsg('Delete failed: ' + (e.response?.data?.message || e.message), true);
    }
    setDeleting(false);
  };

  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const from = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const to   = Math.min(page * pageSize, totalCount);

  const thSx = { background: '#f8fffe', fontWeight: 800, color: '#555',
    fontSize: '0.71rem', padding: '8px 10px', borderBottom: '2px solid #e0f2e9',
    whiteSpace: 'nowrap', textAlign: 'left' };
  const tdSx = { fontSize: '0.75rem', padding: '7px 10px',
    borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' };

  return (
    <Layout title="SMS Logs">
      {success && <Alert severity="success" sx={{ mb: 1.5, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error   && <Alert severity="error"   sx={{ mb: 1.5, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── Action Buttons ── */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button size="small" variant="contained" startIcon={<Add sx={{ fontSize: 15 }}/>}
          onClick={() => navigate('/collections')}
          sx={{ background: `linear-gradient(135deg,${tc},${tc}cc)`, borderRadius: 2, fontWeight: 700, fontSize: '0.77rem' }}>
          Add Collection
        </Button>
        <Button size="small" variant="outlined" startIcon={syncingRates ? <CircularProgress size={13} color="inherit"/> : <SyncAlt sx={{ fontSize: 15 }}/>}
          onClick={handleSyncRates} disabled={syncingRates}
          sx={{ borderRadius: 2, borderColor: tc, color: tc, fontWeight: 700, fontSize: '0.77rem' }}>
          {syncingRates ? 'Syncing...' : 'Sync Rates'}
        </Button>
        {isMobile() && (stats?.pending || 0) > 0 && (
          <Button size="small" variant="contained" disabled={sending}
            startIcon={sending ? <CircularProgress size={13} color="inherit"/> : <PhoneAndroid sx={{ fontSize: 15 }}/>}
            onClick={sendAllViaSim}
            sx={{ background: 'linear-gradient(135deg,#1976d2,#1565c0)', borderRadius: 2, fontWeight: 700, fontSize: '0.77rem' }}>
            {sending ? `Sending ${currentIdx}/${pendingList.length}...` : `Send All via SIM (${stats?.pending})`}
          </Button>
        )}
        {(stats?.pending || 0) > 0 && (
          <Button size="small" variant="contained" disabled={routerBusy || !routerOnline}
            startIcon={routerBusy ? <CircularProgress size={13} color="inherit"/> : <Router sx={{ fontSize: 15 }}/>} 
            onClick={sendAllViaRouter}
            sx={{ background: 'linear-gradient(135deg,#0f766e,#115e59)', borderRadius: 2, fontWeight: 700, fontSize: '0.77rem' }}>
            {routerBusy && currentIdx
              ? `Router ${currentIdx}/${pendingList.length}...`
              : `Send All via Router (${stats?.pending})`}
          </Button>
        )}
        <Chip
          icon={<Router sx={{ fontSize: '15px !important' }}/>} 
          label={routerOnline ? 'Router Online' : 'Router Offline'}
          size="small"
          onClick={() => checkRouter(routerUrl, false)}
          sx={{ fontWeight: 700, color: routerOnline ? '#047857' : '#b91c1c',
            background: routerOnline ? '#ecfdf5' : '#fef2f2',
            border: `1px solid ${routerOnline ? '#a7f3d0' : '#fecaca'}`, cursor: 'pointer' }}
        />
        <Tooltip title="Router SMS settings">
          <IconButton size="small" onClick={() => setRouterSettingsOpen(true)}
            sx={{ border: '1px solid #cbd5e1', borderRadius: 1.5, color: '#475569' }}>
            <Settings sx={{ fontSize: 17 }}/>
          </IconButton>
        </Tooltip>
        {(stats?.failed || 0) > 0 && (
          <Button size="small" variant="outlined" disabled={resetting}
            startIcon={resetting ? <CircularProgress size={13}/> : <Replay sx={{ fontSize: 15 }}/>}
            onClick={handleResetFailed}
            sx={{ borderRadius: 2, borderColor: '#dc2626', color: '#dc2626', fontWeight: 700, fontSize: '0.77rem' }}>
            Reset Failed ({stats?.failed})
          </Button>
        )}
        {((stats?.pending || 0) + (stats?.failed || 0)) > 0 && (
          <Button size="small" variant="outlined" disabled={retrying}
            startIcon={retrying ? <CircularProgress size={13}/> : <Send sx={{ fontSize: 15 }}/>}
            onClick={handleRetry}
            sx={{ borderRadius: 2, borderColor: tc, color: tc, fontWeight: 700, fontSize: '0.77rem' }}>
            {retrying ? 'Retrying...' : `Retry Server`}
          </Button>
        )}
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={refresh}
            sx={{ border: `1px solid ${tc}44`, borderRadius: 1.5, color: tc, ml: 'auto' }}>
            <Refresh fontSize="small"/>
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Desktop Banner ── */}
      {!isMobile() && (stats?.pending || 0) > 0 && !routerOnline && (
        <Alert severity="info" icon={<DesktopWindows/>} sx={{ mb: 2, borderRadius: 2, py: 0.5, fontSize: '0.82rem' }}>
          <strong>{stats?.pending} SMS pending.</strong> Start the local router SMS sender, then click <strong>Router Offline</strong> to reconnect.
        </Alert>
      )}

      {/* ── Stats ── */}
      {stats && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <StatCard label="Total"     value={stats.total     ?? 0} color="#555"    bg="#f9fafb" icon={<Sms sx={{ fontSize: 15 }}/>}/>
          <StatCard label="Pending"   value={stats.pending   ?? 0} color="#d97706" bg="#fffbeb" icon={<HourglassEmpty sx={{ fontSize: 15 }}/>}/>
          <StatCard label="Sent"      value={stats.sent      ?? 0} color="#2563eb" bg="#eff6ff" icon={<Send sx={{ fontSize: 15 }}/>}/>
          <StatCard label="Delivered" value={stats.delivered ?? 0} color="#059669" bg="#ecfdf5" icon={<CheckCircleOutline sx={{ fontSize: 15 }}/>}/>
          <StatCard label="Failed"    value={stats.failed    ?? 0} color="#dc2626" bg="#fef2f2" icon={<ErrorOutline sx={{ fontSize: 15 }}/>}/>
        </Box>
      )}

      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>

        {/* ── Header + Filter ── */}
        <Box sx={{ px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1,
          justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0', background: '#fafffe', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Sms sx={{ color: tc, fontSize: 19 }}/>
            <Typography sx={{ fontWeight: 800, fontSize: '0.97rem' }}>SMS Logs</Typography>
            {(stats?.pending || 0) > 0 && (
              <Chip label={`${stats.pending} Pending`} size="small"
                sx={{ background: '#fff3cd', color: '#d97706', fontWeight: 700, fontSize: '0.67rem', height: 18 }}/>
            )}
          </Box>
          <Select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            size="small" displayEmpty
            sx={{ minWidth: 110, fontSize: '0.77rem', '& .MuiSelect-select': { py: 0.55 } }}>
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="SENT">Sent</MenuItem>
            <MenuItem value="DELIVERED">Delivered</MenuItem>
            <MenuItem value="FAILED">Failed</MenuItem>
          </Select>
        </Box>

        {/* ── Table ── */}
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Farmer', 'Mobile', 'Message', 'Status', 'Time', 'Action'].map(col => (
                  <th key={col} style={thSx}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>{Array.from({ length: 6 }).map((__, j) => (
                      <td key={j} style={tdSx}><Skeleton height={16}/></td>
                    ))}</tr>
                  ))
                : data.length === 0
                  ? (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: '36px 16px' }}>
                        <Message sx={{ fontSize: 44, color: '#e0e0e0', display: 'block', mx: 'auto', mb: 1 }}/>
                        <Typography variant="body2" sx={{ color: '#bbb' }}>No SMS logs found.</Typography>
                      </td>
                    </tr>
                  )
                  : data.map((row, idx) => (
                    <tr key={row.smsId} style={{ background: idx % 2 === 0 ? '#fff' : '#fafffe' }}>
                      <td style={tdSx}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Avatar sx={{ width: 26, height: 26, fontSize: '0.65rem', background: tc + '20', color: tc, fontWeight: 800 }}>
                            {(row.farmerCode || '?').slice(0, 2)}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontWeight: 700, color: tc, fontSize: '0.73rem', lineHeight: 1.2 }}>
                              {row.farmerCode}
                            </Typography>
                            <Typography sx={{ color: '#888', fontSize: '0.66rem' }}>{row.farmerName}</Typography>
                          </Box>
                        </Box>
                      </td>
                      <td style={{ ...tdSx, fontFamily: 'monospace', fontSize: '0.72rem', color: '#374151' }}>
                        {row.mobileNumber}
                      </td>
                      <td style={{ ...tdSx, maxWidth: 240 }}>
                        <Typography sx={{ fontSize: '0.72rem', color: '#444', display: '-webkit-box',
                          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          overflow: 'hidden', lineHeight: 1.4, whiteSpace: 'normal' }}>
                          {row.message}
                        </Typography>
                      </td>
                      <td style={tdSx}>
                        <StatusBadge status={row.status}/>
                        {row.gatewayResponse && row.status === 'FAILED' && (
                          <Tooltip title={row.gatewayResponse}>
                            <Typography sx={{ color: '#dc2626', fontSize: '0.6rem', mt: 0.2,
                              maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {row.gatewayResponse}
                            </Typography>
                          </Tooltip>
                        )}
                      </td>
                      <td style={{ ...tdSx, fontSize: '0.66rem', color: '#888', whiteSpace: 'nowrap' }}>
                        {(row.sentAt || row.createdAt)
                          ? new Date(row.sentAt || row.createdAt).toLocaleString('en-IN', {
                              day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                            })
                          : '—'}
                      </td>
                      <td style={{ ...tdSx, whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {isMobile() && (row.status === 'PENDING' || row.status === 'FAILED') && (
                            <Tooltip title="Send via SIM">
                              <IconButton size="small" onClick={() => sendSingle(row)}
                                sx={{ color: '#1976d2', p: 0.35, border: '1px solid #bfdbfe', borderRadius: 1.5 }}>
                                <Send sx={{ fontSize: 13 }}/>
                              </IconButton>
                            </Tooltip>
                          )}
                          {(row.status === 'PENDING' || row.status === 'FAILED') && (
                            <Tooltip title={routerOnline ? 'Send through Secureye router' : 'Router sender is offline'}>
                              <span>
                                <IconButton size="small" disabled={!routerOnline || routerBusy}
                                  onClick={() => sendSingleViaRouter(row)}
                                  sx={{ color: '#0f766e', p: 0.35, border: '1px solid #99f6e4', borderRadius: 1.5 }}>
                                  <Router sx={{ fontSize: 13 }}/>
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => confirmDelete(row)}
                              sx={{ color: '#e53e3e', p: 0.35, border: '1px solid #fca5a5', borderRadius: 1.5 }}>
                              <DeleteOutline sx={{ fontSize: 13 }}/>
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </Box>

        {/* ── Pagination ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 0.8, borderTop: '1px solid #f0f0f0', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="caption" sx={{ color: '#777' }}>
            {from}–{to} of {totalCount}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
            <IconButton size="small" onClick={() => setPage(1)} disabled={page <= 1}>
              <Typography sx={{ fontSize: 11, fontWeight: 700 }}>«</Typography>
            </IconButton>
            <IconButton size="small" onClick={() => setPage(p => p - 1)} disabled={page <= 1}>
              <Typography sx={{ fontSize: 11, fontWeight: 700 }}>‹</Typography>
            </IconButton>
            <Typography variant="caption" sx={{ px: 0.8, color: '#555', fontWeight: 600 }}>
              {page}/{totalPages}
            </Typography>
            <IconButton size="small" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>
              <Typography sx={{ fontSize: 11, fontWeight: 700 }}>›</Typography>
            </IconButton>
            <IconButton size="small" onClick={() => setPage(totalPages)} disabled={page >= totalPages}>
              <Typography sx={{ fontSize: 11, fontWeight: 700 }}>»</Typography>
            </IconButton>
            <Select value={pageSize} onChange={(e) => { setPageSize(e.target.value); setPage(1); }}
              size="small" sx={{ ml: 0.5, fontSize: '0.73rem', height: 24, minWidth: 52 }}>
              {[10, 20, 50, 100].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </Box>
        </Box>
      </Card>

      {/* ── Info Box ── */}
      <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, background: `${tc}08`, border: `1px solid ${tc}20` }}>
        <Typography variant="caption" sx={{ color: tc, fontWeight: 700, display: 'block', mb: 0.3 }}>
          📡 How It Works
        </Typography>
        <Typography variant="caption" sx={{ color: '#666', lineHeight: 1.8, display: 'block' }}>
          • Save collection → SMS queued as <strong>Pending</strong><br/>
          • <strong>Web:</strong> Use "Send via Router" after the local router sender shows Online<br/>
          • <strong>Mobile:</strong> You can also tap "Send via SIM" to use the phone SIM<br/>
          • <strong>Sync Rates:</strong> Updates today's collection amounts with current rate card<br/>
          • <strong>Delete</strong> any SMS you don't want sent
        </Typography>
      </Box>

      {/* ── Delete Confirm Dialog ── */}
      <Dialog open={deleteDialog} onClose={() => !deleting && setDeleteDialog(false)}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          py: 1.5, px: 2.5, background: '#fff5f5', fontWeight: 700, fontSize: '0.92rem',
          borderBottom: '1px solid #fecaca', color: '#dc2626' }}>
          Delete SMS
          <IconButton size="small" onClick={() => setDeleteDialog(false)} disabled={deleting}>
            <Close fontSize="small"/>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, pt: 2, pb: 1 }}>
          <Typography variant="body2" sx={{ color: '#444', mb: 1.5 }}>
            Delete this SMS? It will not be sent.
          </Typography>
          {deleteTarget && (
            <Box sx={{ p: 1.5, borderRadius: 2, background: '#f9fafb', border: '1px solid #e5e7eb', mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {deleteTarget.farmerName} — {deleteTarget.mobileNumber}
              </Typography>
              <Box sx={{ mt: 0.5 }}><StatusBadge status={deleteTarget.status}/></Box>
            </Box>
          )}
          <Alert severity="warning" sx={{ py: 0.3, borderRadius: 2, fontSize: '0.78rem' }}>
            This cannot be recovered.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2, gap: 1 }}>
          <Button onClick={() => setDeleteDialog(false)} variant="outlined" disabled={deleting}
            sx={{ borderRadius: 2, color: '#888', borderColor: '#ddd', flex: 1 }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" disabled={deleting}
            startIcon={deleting ? <CircularProgress size={13} color="inherit"/> : <DeleteOutline/>}
            sx={{ background: 'linear-gradient(135deg,#dc2626,#b91c1c)', borderRadius: 2, fontWeight: 700, flex: 1 }}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={routerSettingsOpen} onClose={() => setRouterSettingsOpen(false)}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Router color="primary"/> Router SMS Sender
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>
            Address of the local Windows sender connected to the Secureye router.
          </Typography>
          <Box component="input" value={routerUrlDraft}
            onChange={(e) => setRouterUrlDraft(e.target.value)}
            placeholder={DEFAULT_ROUTER_HELPER}
            sx={{ width: '100%', boxSizing: 'border-box', p: 1.2, borderRadius: 1.5,
              border: '1px solid #cbd5e1', fontSize: '0.9rem', outline: 'none' }}/>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRouterSettingsOpen(false)}>Cancel</Button>
          <Button variant="outlined" onClick={() => checkRouter(routerUrlDraft, false)}>Test</Button>
          <Button variant="contained" onClick={saveRouterSettings}>Save</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
